// Split out of src/components/Wizard.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). Wizard.jsx
// re-exports everything it exported before, so existing imports keep working.

import { CoinIcon, Icon } from "../Common.jsx";
import { InstallButton, useInstallPrompt } from "../Onboarding.jsx";
import { genPasscode } from "../../lib/constants.js";
import { t as ct } from "../../lib/content.js";
import { apiRevealJoinToken } from "../../lib/api.js";
import { joinDeepLink, joinQrDataUrl } from "../../lib/qrcode.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- setup complete overlay ------------------------- */

// Shown once, right after the wizard's final "Finish setup" tap — replaces the cold
// landing straight into the Home tab with a short, action-oriented "what now?" screen.
// Deliberately reuses the InstallButton component so the Install action here is real,
// not just a pointer to a button elsewhere in the header.

// Persisted so a parent who ticks "Don't show me this again" doesn't see the overlay a
// second time even if setup is somehow re-triggered — App.jsx checks this before setting
// showSetupComplete to true.
export const SETUP_COMPLETE_SEEN_KEY = "lb_setup_complete_seen";
export function hasSeenSetupComplete() {
  try { return localStorage.getItem(SETUP_COMPLETE_SEEN_KEY) === "1"; } catch { return false; }
}

/* The last screen of setup, rewritten (Alan, 30 Aug 2026).
 *
 * It used to carry three cards — "manage it all from Parent Mode", install, "show your kids
 * the ropes" — under a heading promising what to do next, and then land the parent on Parent
 * Home. Two things were wrong with that. The first card described the screen the parent had
 * been sitting on for six steps and was about to land on again. And the third named an
 * outcome without ever naming the action: it never said Kid Mode, or where that button was.
 *
 * So the showing-around step became the button instead of a bullet, and the button says
 * where it goes. Parent Home is the emptiest screen in the app on day one — no requests, no
 * transactions, balances at zero — while Kid Mode has the welcome card already waiting, per
 * child. That is the only screen that shows a parent what they just built, in the form their
 * child will meet it.
 *
 * What's left is the two things genuinely still to do: install, and get the kids onto their
 * own devices. The second is the one that's hard to discover later, which is why the
 * quick-join QR is here rather than only four levels into Settings.
 */
export function SetupCompleteOverlay({ onClose, onShowKid, code, state, update, verifiedPin, firstChild, preview = false }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [joinToken, setJoinToken] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const [qrError, setQrError] = useState("");
  const [showTyped, setShowTyped] = useState(false);
  // beforeinstallprompt doesn't fire on every browser (desktop Firefox, in-app browsers,
  // once already dismissed on some Chromium builds, etc.) — InstallButton silently renders
  // null in that case, which would otherwise leave this bullet promising a button that
  // never shows up. Drive the copy off the same install-availability state so there's
  // always something actionable here instead of dead space.
  const { installable, isStandalone, installed } = useInstallPrompt();
  const alreadyInstalled = isStandalone || installed;

  function handleClose(then) {
    if (dontShowAgain) {
      try { localStorage.setItem(SETUP_COMPLETE_SEEN_KEY, "1"); } catch {}
    }
    then();
  }

  /* Reveal-on-demand, mirroring Settings → Household → Add a device exactly: the token is
   * fetched with the PIN, minted client-side through the normal update() flow if this
   * household has never needed one, and only ever exists on screen as the QR image.
   *
   * Not shown by default on purpose. Code plus join token is enough to join on its own — no
   * passcode — so the QR is a bearer credential, and this is a screen someone might be
   * holding up to a child. A tap to reveal, and the regenerate route named right underneath,
   * is the same posture Settings takes for the same reason. */
  async function revealQr() {
    if (showQr) { setShowQr(false); return; }
    if (joinToken) { setShowQr(true); return; }
    // V.3: the preview has no household on the Worker, so the join-token call can only fail
    // with a PIN error that looks like a real bug. Say what it is instead.
    if (preview) { setQrError(ct("wizard.complete.qr_preview", "The QR code needs a real household — it isn't available in preview mode.")); return; }
    setQrBusy(true); setQrError("");
    try {
      const pinToProve = state?.settings?.pinConfigured ? verifiedPin : "123456";
      let token = await apiRevealJoinToken(code, pinToProve);
      if (!token) {
        token = genPasscode(16);
        update?.((s) => { s.settings.householdJoinToken = token; return s; });
      }
      setJoinToken(token);
      setShowQr(true);
    } catch (err) {
      setQrError(err?.message || ct("wizard.complete.qr_failed", "Couldn't get the QR just now — you can find it any time in Settings → Household → Add a device."));
    } finally {
      setQrBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 text-center">
          <div className="flex justify-center mb-2">
            <CoinIcon className="w-14 h-14" />
          </div>
          <div className="text-xl font-extrabold text-slate-900 mb-1">{ct("wizard.complete.heading", "You're all set up")}</div>
          <p className="text-sm text-slate-500 mb-5">
            {firstChild
              ? ct("wizard.complete.subheading_child", "Two things left, then you can show {name} around.", { name: firstChild.name })
              : ct("wizard.complete.subheading", "Two things left before you start.")}
          </p>

          <div className="flex flex-col gap-3 text-left mb-5">
            {/* Alignment fix (Alan, 30 Aug 2026). The "Click to install" caption sat UNDER the
                icon inside a centred column, so its width — far wider than a 24px icon —
                drove the column and shunted the body text out of line. The caption now sits
                with the copy it refers to, leaving the icon column as wide as the icon. */}
            <div className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-3">
              <div className="flex-shrink-0 w-6 flex justify-center">
                <Icon id="ic-install" size={24} />
              </div>
              <div className="min-w-0">
                {installable && <div className="text-xs font-bold text-teal-600 leading-none mb-1">Click to install ↓</div>}
                <div className="text-sm font-semibold text-slate-800">
                  {alreadyInstalled ? "Already installed on this device" : installable ? "Install the app" : "Add it to your home screen"}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {alreadyInstalled
                    ? "One tap from your home screen, instead of finding the tab each time."
                    : installable
                    ? "One tap from your home screen, instead of finding the tab each time."
                    : "This browser doesn't offer one-tap install. Open littlebankers.uk in Chrome, Edge, or Safari and look for \"Add to Home Screen\" or \"Install app\" in the browser menu."}
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-3" data-testid="setup-complete-devices">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 flex justify-center"><Icon id="ic-devices" size={22} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">{ct("wizard.complete.devices_title", "Get your kids on their own devices")}</div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                    {ct("wizard.complete.devices_body", "Point their camera at your QR and the join screen opens already filled in. If the app is already on their device, use Scan QR to join on its Join screen instead.")}
                  </p>
                </div>
              </div>
              <button className="w-full mt-2.5 bg-white border border-slate-200 text-slate-600 font-bold text-xs rounded-xl py-2 disabled:opacity-60"
                data-testid="setup-complete-show-qr" disabled={qrBusy} onClick={revealQr}>
                {qrBusy ? "…" : showQr ? ct("wizard.complete.qr_hide", "Hide QR") : ct("wizard.complete.qr_show", "Show QR")}
              </button>
              {qrError && <p className="text-xs text-rose-600 font-semibold mt-2">{qrError}</p>}
              {showQr && joinToken && (
                <div className="mt-2.5 text-center">
                  <img src={joinQrDataUrl(joinDeepLink(code, joinToken))} alt={`QR code to quick-join household ${code}`} className="w-36 h-36 mx-auto rounded-xl border border-slate-200 bg-white" />
                  <p className="text-xs text-slate-400 leading-snug mt-2">
                    {ct("wizard.complete.qr_warning", "Anyone who scans this joins your household, so don't post it or share the screenshot. You can replace it any time in Settings → Household → Add a device.")}
                  </p>
                </div>
              )}
              <button className="w-full mt-1.5 text-slate-400 font-semibold text-xs py-1" data-testid="setup-complete-typed" onClick={() => setShowTyped((v) => !v)}>
                {showTyped ? ct("wizard.complete.typed_hide", "Hide") : ct("wizard.complete.typed_show", "Or type it in instead")}
              </button>
              {showTyped && (
                <p className="text-xs text-slate-500 leading-relaxed bg-white border border-slate-100 rounded-xl p-2.5">
                  {ct("wizard.complete.typed_body", "On each child's device, open littlebankers.uk, tap Join a household, and enter code {code} plus your passcode. Both live in Settings → Household.", { code })}
                </p>
              )}
            </div>
          </div>

          {installable && (
            <div className="flex justify-center mb-4">
              <InstallButton code={code} />
            </div>
          )}

          <label className="flex items-center justify-center gap-2 text-xs text-slate-500 mb-3 cursor-pointer select-none">
            <input type="checkbox" className="w-3.5 h-3.5" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />
            {ct("wizard.complete.dont_show_again", "Don't show me this again")}
          </label>

          {firstChild && onShowKid ? (
            <>
              <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-3" data-testid="setup-complete-show-kid" onClick={() => handleClose(onShowKid)}>
                {ct("wizard.complete.show_kid", "Show {name} around", { name: firstChild.name })}
              </button>
              {/* The one way this button can go badly is worth reading in the same glance as
                  the button itself: the PIN was set one step ago, just before Notifications, and
                  Kid Mode is a one-way door without it. */}
              <p className="text-xs text-slate-400 leading-snug mt-2">
                {ct("wizard.complete.show_kid_note", "Opens Kid Mode on {name}'s home screen. Coming back to Parent Mode will ask for your PIN.", { name: firstChild.name })}
              </p>
              <button className="w-full text-slate-400 font-semibold text-xs py-2 mt-1" data-testid="setup-complete-later" onClick={() => handleClose(onClose)}>
                {ct("wizard.complete.close_button_later", "I'll do this later, take me to Parent Mode")}
              </button>
            </>
          ) : (
            <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-3" data-testid="setup-complete-later" onClick={() => handleClose(onClose)}>
              {ct("wizard.complete.close_button", "Go to Parent Mode")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
